import { Link } from "react-router-dom";
import { useState, useContext } from "react";
import { InputField, MainButton, Loader } from "commonUI";
import "./auth.css";
import { useNavigate } from "react-router-dom";
import { UserAuthContext } from "context/UserAuth";
import { useMutation } from "@tanstack/react-query";
import { activateLicense, useCheckKeyStatus, userLogin } from "api/userAuth";
import { Logo } from "allIcons";
import { ApiResponseType } from "enums";
import { t } from "i18next";
import { checkRolePermission } from "./auth";

export default function Login() {
  const { setUserAuth } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const [userInput, setUserInput] = useState({
    email: "",
    password: "",
    error: "",
  });

  const { mutateAsync: checkKeyStatus, isLoading: checkingKeyStatus } =
    useCheckKeyStatus();

  const handleWorkspaceValidation = (workspaces) => {
    const isUserAllowed = checkRolePermission(
      workspaces.current_user.role.app_permissions
    );
    if (!isUserAllowed) {
      setUserInput({
        ...userInput,
        error: t("workspace.error.noLoginPermission"),
      });
      return false;
    }
    return true;
  };

  const createUserSession = (workspace, data) => {
    localStorage.setItem("apikey", workspace.key);
    localStorage.setItem("user_id", data.user_id);
    localStorage.setItem("token", data.token);
    setUserAuth((pre) => ({
      ...pre,
      token: data.token,
      apikey: workspace.key,
    }));
    navigate("/");
  };

  const loginHandler = (data) => {
    if (data.workspaces.length === 1) {
      const workspace = data.workspaces[0];
      if (!handleWorkspaceValidation(workspace)) return;

      localStorage.setItem("token", data.token);
      checkKeyStatus({ api_key: workspace.key })
        .then((res) => {
          if (res.status_code === ApiResponseType.SUCCESS) {
            createUserSession(workspace, data);
          } else {
            activateLicense({
              api_key: workspace.key,
            })
              .then((res) => {
                if (res.status_code === ApiResponseType.SUCCESS) {
                  createUserSession(workspace, data);
                } else {
                  localStorage.clear("token");
                  setUserInput({
                    ...userInput,
                    error: t("workspace.error.licenseNotActivated"),
                  });
                }
              })
              .catch((err) => {
                localStorage.clear("token");
                setUserInput({
                  ...userInput,
                  error: t("workspace.error.licenseNotActivated"),
                });
              });
          }
        })
        .catch((err) => {
          localStorage.clear("token");
          setUserInput({
            ...userInput,
            error: t("workspace.error.licenseNotActivated"),
          });
        });
    } else {
      localStorage.setItem("user_id", data.user_id);
      localStorage.setItem("token", data.token);
      setUserAuth((pre) => ({
        ...pre,
        token: data.token,
      }));
      setUserInput({
        ...userInput,
        error: "",
      });
      navigate("/user-accounts");
    }
  };

  const mutation = useMutation(userLogin, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.CONFLICT) {
        setUserInput({
          ...userInput,
          error: data.message,
        });
        return;
      }
      if (data.data.token) {
        loginHandler(data.data);
      } else {
        setUserInput({
          ...userInput,
          error: t("login.api.error.invalidEmailOrPassword"),
        });
      }
    },
    onError: (error) => {
      setUserInput({
        ...userInput,
        error: t("login.api.error.invalidEmailOrPassword"),
      });
    },
  });

  return (
    <>
      <Loader show={mutation.isLoading || checkingKeyStatus} />
      <div className={`login-container`}>
        <div className="">
          <div
            className={`flex items-center bg-transparent mb-[87px] justify-center`}
          >
            <Logo className="logo-size" />
          </div>
          <div className="login-form">
            <h2 className="login-heading w-full">{t("login.title")}</h2>
            <div className="py-4 w-fit mx-auto">
              {userInput.error && (
                <p className="error-text mb-1">{userInput.error}</p>
              )}
              <InputField
                type="text"
                name="email"
                id="email_address"
                placeholder={t("login.input.email.placeholder")}
                value={userInput.email}
                setValue={(value) =>
                  setUserInput({ ...userInput, email: value, error: "" })
                }
                inputClass="py-1"
              />
              <InputField
                type="password"
                name="password"
                id="password"
                placeholder={t("login.input.password.placeholder")}
                value={userInput.password}
                setValue={(value) =>
                  setUserInput({ ...userInput, password: value, error: "" })
                }
                inputClass="py-1"
              />
              <div className="my-2">
                <Link to={"/forgot-password"} className="forget-link">
                  {t("login.button.forgetPassword")}
                </Link>
              </div>
              <div className="py-2">
                <MainButton
                  onClick={() =>
                    mutation.mutate({
                      email: userInput.email,
                      password: userInput.password,
                    })
                  }
                  className={`w-full py-2 `}
                >
                  {t("login.button.login")}
                </MainButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
